import * as React from 'react'
import { X } from 'lucide-react'

import { cn } from '../../lib/utils'

type ToastVariant = 'default' | 'success' | 'danger'

interface ToastItem {
  id: number
  message: string
  variant: ToastVariant
}

interface ToastContextValue {
  toast: (message: string, variant?: ToastVariant) => void
}

const ToastContext = React.createContext<ToastContextValue | null>(null)

/** Throws rather than no-oping: a silent toast is a bug you find in production. */
export function useToast() {
  const context = React.useContext(ToastContext)
  if (!context) throw new Error('useToast must be used inside <ToastProvider>')
  return context
}

const DURATION = 3200

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [items, setItems] = React.useState<ToastItem[]>([])
  const timers = React.useRef(new Set<ReturnType<typeof setTimeout>>())

  React.useEffect(() => {
    const pending = timers.current
    return () => pending.forEach(clearTimeout)
  }, [])

  const dismiss = React.useCallback((id: number) => {
    setItems((previous) => previous.filter((item) => item.id !== id))
  }, [])

  const toast = React.useCallback(
    (message: string, variant: ToastVariant = 'default') => {
      const id = Date.now() + Math.random()
      setItems((previous) => [...previous, { id, message, variant }])
      const timer = setTimeout(() => {
        dismiss(id)
        timers.current.delete(timer)
      }, DURATION)
      timers.current.add(timer)
    },
    [dismiss],
  )

  const value = React.useMemo(() => ({ toast }), [toast])

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div
        aria-live="polite"
        className="pointer-events-none fixed right-4 bottom-4 z-80 flex flex-col gap-2"
      >
        {items.map((item) => (
          <div
            key={item.id}
            className={cn(
              'shadow-pop pointer-events-auto flex min-w-64 items-center gap-2 rounded-lg border px-3 py-2 text-sm',
              'animate-in slide-in-from-bottom-2 fade-in duration-150',
              item.variant === 'success'
                ? 'bg-ok-surface text-ok border-transparent'
                : item.variant === 'danger'
                  ? 'bg-err-surface text-err border-transparent'
                  : 'border-border bg-bg-elev text-fg',
            )}
          >
            <span className="flex-1">{item.message}</span>
            <button
              type="button"
              aria-label="Dismiss"
              onClick={() => dismiss(item.id)}
              className="focus-visible:outline-accent shrink-0 rounded-sm opacity-60 transition-opacity duration-100 ease-out outline-none hover:opacity-100 focus-visible:outline-2 focus-visible:outline-offset-1"
            >
              <X className="size-3" />
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  )
}

export type { ToastVariant }
